import AlbumService from "@/services/Album";
import Vue from "vue";

const state = {
  id: null,
  coverArt: "",
  title: "",
  artistName: "",
  genre: "alt",
  language: "",
  selectedSongs: [],
  languages: [],
  genres: {},
  genresArray: [],
  coverArtWord: "",
  fetchingCoverArt: false,
  coverArtCreditURL: "",
  coverArtCreditName: "",
  submittingCoverArt: false,
  artistNameSearch: "",
  artistNameError: "",
  genreError: "",
  explicitError: "",
  titleError: "",
  coverArtError: "",
  languageError: "",
  explicit: "No Lyrics",
  explicitArray: [],
  issues: {},
  albumSongs: {
    fetching: false,
    fetchedAt: 0,
    error: false
  }
};

const mutations = {
  SET_ALBUM_DATA(
    state,
    { id, coverArt, title, artistName, genre, language, explicit, issues }
  ) {
    state.id = id;
    state.coverArt = coverArt;
    state.title = title;
    state.artistName = artistName;
    state.genre = genre;
    state.language = language;
    state.explicit = explicit;
    Vue.set(state, "issues", issues);
  },
  RESET_ALBUM_DATA(state) {
    state.id = null;
    state.coverArt = "";
    state.title = "";
    state.artistName = "";
    state.genre = "alt";
    state.language = "";
    state.selectedSongs = [];
    state.coverArtWord = "";
    state.fetchingCoverArt = false;
    state.coverArtCreditURL = "";
    state.coverArtCreditName = "";
    state.submittingCoverArt = false;
    state.artistNameSearch = "";
    state.artistNameError = "";
    state.titleError = "";
    state.languageError = "";
    state.coverArtError = "";
    Vue.set(state, "issues", {});
    state.explicit = state.explicitArray[0].value;
  },
  SET_ALBUM_ID(state, albumId) {
    state.id = albumId;
  },
  SET_COVER_ART(state, coverArt) {
    state.coverArt = coverArt;
  },
  SET_TITLE(state, title) {
    state.title = title;
  },
  SET_ARTIST_NAME(state, artistName) {
    state.artistName = artistName;
  },
  SET_GENRE(state, genre) {
    state.genre = genre;
  },
  SET_LANGUAGE(state, language) {
    state.language = language;
  },
  SET_COVER_ART_WORD(state, coverArtWord) {
    state.coverArtWord = coverArtWord;
  },
  SET_FETCHING_COVER_ART(state, fetchingCoverArt) {
    state.fetchingCoverArt = fetchingCoverArt;
  },
  SET_COVER_ART_CREDIT_NAME(state, name) {
    state.coverArtCreditName = name;
  },
  SET_COVER_ART_CREDIT_URL(state, url) {
    state.coverArtCreditURL = url;
  },
  SET_SELECTED_SONGS(state, selectedSongs) {
    state.selectedSongs = selectedSongs;
  },
  SET_ARTIST_NAME_SEARCH(state, searchTerm) {
    state.artistNameSearch = searchTerm;
  },
  SET_ARTIST_NAME_ERROR(state, artistNameError) {
    state.artistNameError = artistNameError;
  },
  SET_GENRE_ERROR(state, genreError) {
    state.genreError = genreError;
  },
  SET_EXPLICIT_ERROR(state, explicitError) {
    state.explicitError = explicitError;
  },
  SET_TITLE_ERROR(state, titleError) {
    state.titleError = titleError;
  },
  SET_LANGUAGE_ERROR(state, languageError) {
    state.languageError = languageError;
  },
  SET_COVER_ART_ERROR(state, coverArtError) {
    state.coverArtError = coverArtError;
  },
  SET_GENRES(state, genres) {
    state.genres = genres;
  },
  SET_GENRES_ARRAY(state, genresArray) {
    state.genresArray = genresArray;
  },
  SET_EXPLICIT(state, explicit) {
    state.explicit = explicit;
  },
  SET_EXPLICIT_ARRAY(state, explicitArray) {
    state.explicitArray = explicitArray;
  },
  SET_LANGUAGES(state, languages) {
    state.languages = languages;
  },
  SET_SUBMITTING_COVER_ART(state, submitting) {
    state.submittingCoverArt = submitting;
  },
  SET_ALBUM_SONGS_FETCHING({ albumSongs }, isFetching) {
    albumSongs.fetching = isFetching;
  },
  SET_ALBUM_SONGS_ERROR({ albumSongs }, hasError) {
    albumSongs.error = hasError;
  },
  SET_ALBUM_SONGS(state, { songs, fetchedAt }) {
    songs.forEach(song => {
      if (typeof song.id === "string") {
        song.id = parseInt(song.id);
        song.duration = song.durationMs / 1000;
      }
    });
    state.selectedSongs = songs;
    state.albumSongs.fetchedAt = fetchedAt;
  },
  CLEAR_ALBUM_ISSUES(state) {
    Vue.set(state, "issues", {});
  }
};

const actions = {
  async fetchAlbumInfo(context, albumId) {
    const res = await AlbumService.getAlbumInfo(albumId);
    if (res.status === 200 && res.data) {
      let {
        id,
        coverArt,
        albumTitle: title,
        artistName,
        genre,
        language,
        explicit,
        issues
      } = res.data || {};
      if (language === "None") {
        explicit = "No Lyrics";
      } else {
        explicit = explicit === 1 ? "Explicit" : "Clean";
      }
      issues = issues || {};

      context.commit("SET_ALBUM_DATA", {
        id,
        coverArt,
        title,
        artistName,
        genre,
        language: language === "None" ? "" : language,
        explicit,
        issues
      });
    }
  },
  async createOrUpdateAlbum(context, album) {
    if (album.language === "" || album.explicit === "No Lyrics") {
      album.language = "None";
    }
    if (context.state.id) {
      await AlbumService.updateAlbum(context.state.id, album);
    } else {
      const result = await AlbumService.createAlbum(album);
      context.commit("SET_ALBUM_ID", result.data.id);
    }
  },
  addSongsToAlbum(context, { albumId, songIds }) {
    return AlbumService.postAddAlbumSongs(albumId, songIds);
  },
  async submitAlbum(context, albumId) {
    const response = await AlbumService.postSubmitAlbum(albumId);
    context.dispatch("user/getUserOnboardingSteps", {}, { root: true });
    return response;
  },
  async checkArtistExists(context, artistName) {
    const result = await AlbumService.getArtistExists(artistName);
    return result.data.exists !== 0;
  },
  async fetchCoverArt({ commit, state }, { loggedInUserId }) {
    commit("SET_COVER_ART_ERROR", "");
    const word = state.coverArtWord;
    if (word.split(" ").length > 1) {
      const aiCoverError =
        "Please enter just one english word to inspire cover art.";
      commit("SET_COVER_ART_ERROR", aiCoverError);
      return;
    }
    commit("SET_SUBMITTING_COVER_ART", true);
    const result = await AlbumService.postFetchCoverArt(word);
    if (result.data.success) {
      commit("SET_SUBMITTING_COVER_ART", false);
      this._vm.$amplitude.logEvent("ai_cover_art_success", {});
      const filename = result.data.success;
      let base = filename.substring(filename.lastIndexOf("/") + 1);
      if (base.lastIndexOf(".") !== -1) {
        base = base.substring(0, base.lastIndexOf("."));
      }
      const res = base.split("_");
      const username = res[0].replace(/\.[^/.]+$/, "");
      const creditURL = `https://unsplash.com/@${username}?utm_source=boomy&utm_medium=referral`;
      commit("SET_COVER_ART", filename);
      commit("SET_COVER_ART_CREDIT_NAME", username);
      commit("SET_COVER_ART_CREDIT_URL", creditURL);
    } else {
      this._vm.$amplitude.logEvent("ai_cover_art_fail", {});
      const aiCoverError =
        "Sorry, there was an error processing your request. This might happen if you picked an obscure word. Try again.";
      commit("SET_COVER_ART_ERROR", aiCoverError);
      commit("SET_SUBMITTING_COVER_ART", false);
    }
  },
  setSelectedSongs(context, selectedSongs) {
    context.commit("SET_SELECTED_SONGS", selectedSongs);
  },
  setArtistName(context, artistName) {
    context.commit("SET_ARTIST_NAME", artistName);
    context.commit("SET_ARTIST_NAME_ERROR", "");
  },
  setTitle(context, title) {
    context.commit("SET_TITLE", title);
    context.commit("SET_TITLE_ERROR", "");
  },
  setCoverArt(context, coverArt) {
    context.commit("SET_COVER_ART", coverArt);
    context.commit("SET_COVER_ART_ERROR", "");
  },
  setLanguage(context, language) {
    context.commit("SET_LANGUAGE", language);
    context.commit("SET_LANGUAGE_ERROR", "");
  },
  setExplicit({ commit }, explicit) {
    commit("SET_EXPLICIT", explicit);
  },
  setExplicitArray(context, explicitArray) {
    context.commit("SET_EXPLICIT_ARRAY", explicitArray);
  },
  setGenre(context, genre) {
    context.commit("SET_GENRE", genre);
  },
  setLanguages(context, languages) {
    context.commit("SET_LANGUAGES", languages);
  },
  setGenres(context, genres) {
    context.commit("SET_GENRES", genres);
  },
  setGenresArray(context, genresArray) {
    context.commit("SET_GENRES_ARRAY", genresArray);
  },
  setCoverArtWord(context, coverArtWord) {
    context.commit("SET_COVER_ART_WORD", coverArtWord);
  },
  setArtistNameSearch(context, searchTerm) {
    context.commit("SET_ARTIST_NAME_SEARCH", searchTerm);
  },
  setCoverArtError(context, coverArtError) {
    context.commit("SET_COVER_ART_ERROR", coverArtError);
  },
  setArtistNameError(context, artistNameError) {
    context.commit("SET_ARTIST_NAME_ERROR", artistNameError);
  },
  setGenreError(context, genreError) {
    context.commit("SET_GENRE_ERROR", genreError);
  },
  setExpicitError(context, explicitError) {
    context.commit("SET_EXPLICIT_ERROR", explicitError);
  },
  setTitleError(context, titleError) {
    context.commit("SET_TITLE_ERROR", titleError);
  },
  setLanguageError(context, languageError) {
    context.commit("SET_LANGUAGE_ERROR", languageError);
  },
  setIssues(context, issues) {
    context.commit("SET_ALBUM_ISSUES", issues);
  },
  resetAlbumData(context) {
    context.commit("RESET_ALBUM_DATA");
  },
  clearAlbumIssues(context) {
    context.commit("CLEAR_ALBUM_ISSUES");
  },
  async fetchAlbumSongIds({ commit, dispatch }, albumId) {
    commit("SET_ALBUM_SONGS_FETCHING", true);
    const res = await AlbumService.getAlbumSongs(albumId);
    commit("SET_ALBUM_SONGS_FETCHING", false);
    if (res.status === 200 && res.data) {
      commit("SET_ALBUM_SONGS", {
        songs: res.data,
        fetchedAt: Date.now()
      });
      commit("SET_ALBUM_SONGS_ERROR", false);
    } else {
      commit("SET_ALBUM_SONGS_ERROR", true);
    }
  }
};

const getters = {};

export default {
  namespaced: true,
  state,
  actions,
  getters,
  mutations
};
